import React, { useRef, useState } from 'react';
import { path, prop } from 'ramda';

import { Jackpot } from '@interlayer/js/app/components/common/Jackpot';
import { HoverBlock } from './components/HoverBlock';
import { WrapTileWithTitle } from './components/WrapTileWithTitle';

import { GameSashes } from 'js/app/components/main/Games/components/GameTile/components/GameSashes';

import { noop } from '@core/js/utils/methods.util';

import { GAME_EVENTS } from 'js/app/components/main/Games/constants';
import { useGameComponentConfig } from 'js/app/components/main/Games/hooks/useGameComponentConfig';
import { useGameEventDispatch } from 'js/app/components/main/Games/hooks/useGameEventDispatch';
import { useAutoGameLaunch } from 'js/app/components/main/Games/hooks/useGameLaunch';
import { clsx } from 'js/app/utils/clsx.util';
import { useAppSelector, useUserSelector } from 'js/store/hooks';
import { clsb } from 'js/utils/classNames.util';

import { GameBands } from '../GameBands';

import { useBackgroundTile } from './hooks/useBackgroundTile';

import style from './DetailedTile.scss';

export const DetailedTile = ({
    index,
    gameData: gameDataProp,
    gameCode,
    viewModeInline,
    classNames = {},
    isShowJackpots: isShowJackpotsProp,
    withBands,
    inlineGrid,
    categoryName,
    cb = noop,
    isTitle,
    children: title,
    isShowSashes = true,
    containerRef,
    isForcedOnScreen: isForcedShown,
    disableHoverEffect,
    isShowFavourites,
    isShowInfo,
    gameCodes,
    feedName
}) => {
    const isLoggedIn = useUserSelector(prop('isLoggedIn'));
    const gameEventDispatch = useGameEventDispatch();
    const gameTileRef = useRef(null);
    const gameDataByCode = useAppSelector(path(['gamesConfiguration', gameCode]));
    const gameData = gameDataProp || gameDataByCode || {};
    const launchGame = useAutoGameLaunch({ ...gameData, cb, feedName });
    const [isJackpotActive, setIsJackpotActive] = useState(false);
    const {
        showBands: isShowBands = withBands,
        showJackpots: isShowJackpots = isShowJackpotsProp
    } = useGameComponentConfig();

    const { backgroundData, tileSize } = useBackgroundTile({
        isForcedShown,
        gameData,
        inlineGrid,
        gameTileRef,
        windowRef: containerRef
    });

    const buildCls = clsb([style, classNames]);

    const handlerLaunchGame = () => {
        launchGame();

        if (index) {
            gameEventDispatch({
                name: GAME_EVENTS.GAME_POSITION,
                data: {
                    categoryName,
                    game: gameData.name,
                    gameCode: gameData.gameCode,
                    position: index + 1
                }
            });
        }
    };

    return (
        <WrapTileWithTitle
            isTitle={isTitle}
            title={title}
            feedName={feedName}
            gameCodes={gameCodes}
            gameData={gameData}
            classNames={classNames}
            viewModeInline={viewModeInline}
            handlerLaunchGame={handlerLaunchGame}
        >
            <div
                ref={gameTileRef}
                data-game-code={gameData.gameCode}
                className={clsx(
                    buildCls(['gameTile']),
                    style[`tile${tileSize}`],
                    {
                        [style.isInlineMode]: viewModeInline,
                        [style.isDiamondBorder]: gameData.popular && isLoggedIn
                    }
                )}
                style={backgroundData}
                onClick={handlerLaunchGame}
            >
                {isShowSashes && (
                    <GameSashes additionalAssets={gameData.additionalAssets || []} />
                )}

                {isShowJackpots && (
                    <Jackpot
                        gameCode={gameData.gameCode}
                        className={buildCls(['jackpot'])}
                        setIsJackpotActive={setIsJackpotActive}
                    />
                )}

                {isShowBands && !isJackpotActive && (
                    <GameBands
                        gameData={gameData}
                        viewStyle="detailed"
                        isDiamondEnabled
                    />
                )}

                {!disableHoverEffect && !isTitle && (
                    <HoverBlock
                        feedName={feedName}
                        gameData={gameData}
                        gameCodes={gameCodes}
                        classNames={classNames}
                        containerRef={gameTileRef}
                        isShowInfo={isShowInfo}
                        isShowFavourites={isShowFavourites}
                    />
                )}
            </div>
        </WrapTileWithTitle>
    );
};
